import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { ParamMap, Router, UrlTree } from '@angular/router';
import { Asset } from '../models/asset.model';
import { AssetService } from './asset.service';
import {
  parseSpaceRouteParams,
  spacePublicPathCommands,
  spacePublicPathWithSuffix,
} from '../shared/location/space-public-url.utils';

/** sessionStorage key: survives login redirect to `/espacio/{id}/reservar`. */
export const PENDING_BOOKING_SPACE_ID_KEY = 'tfr_pending_booking_space_id';

@Injectable({ providedIn: 'root' })
export class SpacePublicRouteService {
  private assets = inject(AssetService);
  private router = inject(Router);
  private auth = inject(Auth);
  private platformId = inject(PLATFORM_ID);

  parseRoute(paramMap: ParamMap) {
    return parseSpaceRouteParams(paramMap);
  }

  commandsFor(space: Asset, suffix?: 'calendario' | 'reservar'): string[] {
    return suffix ? spacePublicPathWithSuffix(space, suffix) : spacePublicPathCommands(space);
  }

  canonicalUrlTree(space: Asset, suffix?: 'calendario' | 'reservar'): UrlTree {
    return this.router.createUrlTree(this.commandsFor(space, suffix));
  }

  /**
   * Stable booking entry URL (`/espacio/{firestoreId}/reservar`).
   * Used after login so slug resolution cannot 404 the flow.
   */
  bookingUrlTree(space: Asset): UrlTree {
    const id = space.id?.trim();
    if (id) {
      return this.router.createUrlTree(['/espacio', id, 'reservar']);
    }
    return this.canonicalUrlTree(space, 'reservar');
  }

  rememberPendingBookingSpace(spaceId: string | undefined): void {
    const id = spaceId?.trim();
    if (!id || !isPlatformBrowser(this.platformId)) return;
    sessionStorage.setItem(PENDING_BOOKING_SPACE_ID_KEY, id);
  }

  async loadPendingBookingSpace(): Promise<Asset | null> {
    if (!isPlatformBrowser(this.platformId)) return null;
    const id = sessionStorage.getItem(PENDING_BOOKING_SPACE_ID_KEY)?.trim();
    if (!id) return null;
    sessionStorage.removeItem(PENDING_BOOKING_SPACE_ID_KEY);
    return this.assets.getSpaceById(id);
  }

  /**
   * Replaces legacy `/espacio/{id}` URLs with the slug-based canonical path.
   * Skipped for owner tools (calendar by id) — drafts may not resolve via public slug.
   */
  redirectToCanonicalIfNeeded(
    space: Asset,
    suffix?: 'calendario' | 'reservar',
    options?: { skipForOwnerTools?: boolean },
  ): void {
    if (options?.skipForOwnerTools) return;
    // Only redirect when Firestore already stores the SEO slug (avoids 404 after login).
    if (!space.slugCompleto?.trim()) return;

    const target = this.router.serializeUrl(this.canonicalUrlTree(space, suffix));
    const current = this.router.url.split('?')[0];
    if (current !== target) {
      this.router.navigateByUrl(this.canonicalUrlTree(space, suffix), { replaceUrl: true });
    }
  }

  /** Owner dashboard: always `/espacio/{firestoreId}/calendario` (works for drafts). */
  navigateToOwnerCalendar(spaceId: string): void {
    const id = spaceId.trim();
    if (!id) return;
    void this.router.navigate(['/espacio', id, 'calendario']);
  }

  async loadSpaceFromRoute(paramMap: ParamMap): Promise<Asset | null> {
    const parsed = parseSpaceRouteParams(paramMap);
    if (!parsed) return null;
    if (parsed.kind === 'legacy-id') {
      return this.assets.getSpaceById(parsed.documentId);
    }
    const slugSpace = await this.assets.getSpaceBySlugCompleto(parsed.slugCompleto);
    if (slugSpace) return slugSpace;
    const uid = this.auth.currentUser?.uid;
    if (uid) {
      return this.assets.getOwnerSpaceByPublicSlug(uid, parsed.slugCompleto);
    }
    return null;
  }

  async navigateToSpaceById(
    spaceId: string,
    options?: { suffix?: 'calendario' | 'reservar'; replaceUrl?: boolean },
  ): Promise<void> {
    const space = await this.assets.getSpaceById(spaceId);
    if (!space) {
      this.router.navigate(['/404']);
      return;
    }
    this.router.navigate(this.commandsFor(space, options?.suffix), {
      replaceUrl: options?.replaceUrl,
    });
  }
}
